import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import cameras from "../data/cameras";
import cameraTypes from "../data/camera-types.json";
import scenarios from "../data/scenarios.json";
import lightingConditions from "../data/lighting.json";
import curatedCombos from "../data/recipes-curated.json";
import Footer from "../components/Footer";
import SplitPanel from "../components/SplitPanel";
import { imageForLighting } from "../utils/lightingImages";
import Icon from "../components/Icon";
import { gradientForLighting } from "../utils/lightingGradients";
import { usePublished } from "../utils/publishing";
import { setPageMeta, breadcrumbLd } from "../utils/meta";

/**
 * Every recipe for one camera TYPE — a middle layer between the full
 * gallery (grouped by what you're shooting, across every camera) and a
 * single camera's hub (everything for one specific body).
 *
 * This exists because "camera type" is a real, distinct search shape —
 * "best drone settings" and "best action camera settings" are queries
 * nobody's individual camera hub answers, and the main gallery answers
 * them poorly too, since it's organized by scenario and scatters a
 * drone's recipes across ten different category sections. This page
 * groups the other way: every recipe for every drone, in one place,
 * cross-linked to the specific bodies within it.
 *
 * Same crawling argument as Gallery.jsx: every link here exists in the
 * DOM unconditionally, nothing is behind a click, and this is prerendered
 * per type so a crawler gets real content on first request.
 */
// A representative lighting mood per camera type, so each type page's image
// panel is themed rather than generic. Presentation only.
const TYPE_MOOD = {
  "consumer-drone": "full-sun",
  "prosumer-drone": "golden-hour",
  "fpv-drone": "partly-cloudy",
  "action-camera": "partly-cloudy",
  "360-action-camera": "shade",
  "smartphone": "indoor-bright",
  "compact-gimbal-camera": "overcast",
  "mirrorless": "golden-hour",
  "mirrorless-aps-c": "sunset",
  "cinema-camera": "blue-hour",
};

export default function TypeGallery() {
  const { type } = useParams();
  const { items: live } = usePublished(curatedCombos);

  const typeDef = cameraTypes.find((t) => t.id === type);
  const camerasOfType = useMemo(
    () => cameras.filter((c) => c.type === type && c.capabilityLevel === "full"),
    [type]
  );
  const cameraIds = useMemo(() => new Set(camerasOfType.map((c) => c.id)), [camerasOfType]);

  const combosForType = useMemo(
    () => live.filter((combo) => cameraIds.has(combo.cameraId)),
    [live, cameraIds]
  );

  const byCategory = useMemo(() => {
    const groups = {};
    for (const combo of combosForType) {
      const scenario = scenarios.find((s) => s.id === combo.scenarioId);
      const camera = cameras.find((c) => c.id === combo.cameraId);
      const lighting = lightingConditions.find((l) => l.id === combo.lightingId);
      if (!scenario || !camera || !lighting) continue;
      const key = scenario.category || "Other";
      (groups[key] = groups[key] || []).push({ combo, scenario, camera, lighting });
    }
    Object.values(groups).forEach((list) =>
      list.sort((a, b) => a.camera.name.localeCompare(b.camera.name) || a.scenario.name.localeCompare(b.scenario.name))
    );
    return groups;
  }, [combosForType]);

  const categories = Object.keys(byCategory).sort();

  useEffect(() => {
    if (!typeDef) return;
    setPageMeta({
      title: `${typeDef.name} Settings, Every Recipe`,
      description: `${combosForType.length} camera settings recipes across ${camerasOfType.length} ${typeDef.singular}${camerasOfType.length === 1 ? "" : "s"}, calculated from confirmed specs.`,
      path: `/gallery/${type}/`,
      jsonLdId: "type-gallery-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage",
            name: `${typeDef.name} settings`,
            description: typeDef.description,
          },
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "All recipes", path: "/gallery/" },
            { name: typeDef.name, path: `/gallery/${type}/` },
          ]),
        ],
      },
    });
  }, [typeDef, type, combosForType.length, camerasOfType.length]);

  if (!typeDef) {
    return (
      <main className="gallery-page">
        <h1>Camera type not found</h1>
        <p><Link to="/gallery">Back to all settings</Link></p>
      </main>
    );
  }

  const mood = TYPE_MOOD[typeDef.id] || "overcast";
  return (
    <>
    <SplitPanel
      gradient={gradientForLighting(mood)}
      image={imageForLighting(mood)}
      imageText={{ eyebrow: "Camera type", title: typeDef.name, sub: `${combosForType.length} recipe${combosForType.length === 1 ? "" : "s"}` }}
    >
    <main className="gallery-page split-article">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span>{" "}
        <Link to="/gallery">Settings</Link> <span aria-hidden="true">/</span>{" "}
        <span>{typeDef.name}</span>
      </nav>

      <header className="gallery-header">
        <p className="eyebrow">
          {combosForType.length} recipe{combosForType.length === 1 ? "" : "s"} · {camerasOfType.length}{" "}
          {typeDef.singular}
          {camerasOfType.length === 1 ? "" : "s"}
        </p>
        <h1>{typeDef.name} Settings</h1>
        <p className="gallery-sub">{typeDef.description}</p>
      </header>

      {camerasOfType.length > 0 && (
        <nav className="type-camera-list" aria-label="Cameras in this category">
          {camerasOfType.map((c) => (
            <Link key={c.id} to={`/cameras/${c.id}`} className="type-camera-chip">
              {c.name}
            </Link>
          ))}
        </nav>
      )}

      {categories.length === 0 && (
        <p className="gallery-empty">
          No recipes are published yet for {typeDef.name.toLowerCase()}.{" "}
          <Link to="/gallery">Browse everything else</Link> instead.
        </p>
      )}

      {categories.map((category) => {
        const anchor = `cat-${category.toLowerCase().replace(/\s+/g, "-")}`;
        return (
          <section className="gallery-category" key={category} aria-labelledby={anchor}>
            <div className="category-head">
              <h2 id={anchor}>{category}</h2>
              <span className="category-count data">{byCategory[category].length}</span>
            </div>

            <ul className="scene-grid">
              {byCategory[category].map(({ combo, scenario, camera, lighting }) => {
                const grad = gradientForLighting(combo.lightingId);
                const covers = combo.covers ? combo.covers.length : 0;
                return (
                  <li key={combo.slug}>
                    <Link
                      to={`/camera/${combo.slug}`}
                      className={`scene-card grain text-${grad.text}`}
                      style={{ "--grad-from": grad.from, "--grad-to": grad.to }}
                    >
                      <span className="scene-camera">{camera.name}</span>
                      <span className="scene-title">{scenario.name}</span>
                      <span className="scene-light">
                        <Icon name="light" size={13} /> {lighting.name}
                      </span>
                      {covers > 0 && (
                        <span className="scene-covers">+{covers} more {covers === 1 ? "situation" : "situations"}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      <div data-prerender-ready="true" style={{ display: "none" }} />
    </main>
    </SplitPanel>
    <Footer />
    </>
  );
}
